/**
 * Every Firestore path the CRM touches, in one place.
 *
 * Additive-only contract: the CRM *writes* exclusively under the `skidaCrm`
 * prefix. The product collections listed under PRODUCT_COLLECTIONS are read
 * server-side by Cloud Functions and are never written to by this app.
 */

export const CRM_COLLECTIONS = {
  leads: 'skidaCrmLeads',
  activities: 'skidaCrmActivities',
  settings: 'skidaCrmSettings',
  users: 'skidaCrmUsers',
  /** Each ambassador's app account and Premium, from RevenueCat. See ambassadorAccess.ts. */
  ambassadorAccess: 'skidaCrmAmbassadorAccess',
  /** Country and language per app user, from RevenueCat. See functions-webhook/src/customerFacts.ts. */
  customerFacts: 'skidaCrmCustomerFacts',
  /** Totals the engine keeps; the client reads only `customerFacts` here. */
  rollupState: 'skidaCrmRollupState',
  /**
   * RevenueCat-fed subscription state — one document per **subscription**,
   * keyed by the store's subscription identity. One account can hold several.
   */
  subscriptions: 'skidaCrmSubscriptions',
  /** One document per person, so "how many subscribers" stays answerable. */
  subscribers: 'skidaCrmSubscribers',
  /** Append-only RevenueCat event history. Keyed by the RevenueCat event id. */
  subscriptionEvents: 'skidaCrmSubscriptionEvents',
  /**
   * Firebase Auth's `lastSignInTime` for the guides inside Skida PRO companies,
   * mirrored in by `scripts/syncProMembers.ts`. Auth records are not readable
   * from a browser at all, so this is the only route to "when did this guide
   * last sign in". CRM-owned despite describing product users.
   */
  proMembers: 'skidaCrmProMembers',
  /**
   * Newsletters, at every point in their life — draft, scheduled, and sent.
   *
   * One collection rather than two: the design's list mixes them and its modal
   * switches on a status field, and splitting them would mean a document has to
   * move between collections mid-send. The rules do the separating instead —
   * a client may write a draft and may never touch a sent record.
   */
  emailSends: 'skidaCrmEmailSends',
  /** Publishing plan — Skida's own channel posts. */
  posts: 'skidaCrmPosts',
  /** Partners: organisations we work with rather than sell to. */
  partners: 'skidaCrmPartners',
  /**
   * Owned-channel follower and reach figures, one document per channel.
   * Written only by the scheduled collector — a platform API cannot be called
   * from a browser without exposing its token.
   */
  channelStats: 'skidaCrmChannelStats',
  /** Instagram posts that tagged us, attributed by exact handle match. */
  mentions: 'skidaCrmMentions',
} as const

/** Document ids inside `skidaCrmSettings`. */
export const SETTINGS_DOCS = {
  pipeline: 'pipeline',
  templates: 'templates',
  /**
   * The company's own facts — legal name, org number, par value, bank account
   * and the board. Read by the documents that have to state them; see
   * `types/company.ts` for why par value cannot be derived from the register.
   */
  company: 'company',
} as const

/**
 * Existing collections the CRM reads but never writes.
 *
 * `users` is shared between consumer Skida and Skida PRO, and is the one
 * collection read from the *browser* — awarenessRepository queries it by email
 * for the "Knows Skida" check. That read needs an added Firestore rule; see
 * block 3 of firestore.rules.crm.snippet.
 *
 * The rest are read server-side only, from the Cloud Functions.
 */
export const PRODUCT_COLLECTIONS = {
  users: 'users',
  companies: 'skidaProCompanies',
  companyMembers: 'skidaProCompanyMembers',
  billingSubscriptions: 'billingSubscriptions',
  /**
   * Campaign / promo codes. Read from the browser for the analytics surface.
   * Redemption counts are denormalised onto each code document, so reporting on
   * them needs no group-by and no user reads.
   */
  promoCodes: 'PromoCodes',
  /**
   * Per-user activity, both keyed by an `owner` uid. Read from the browser for
   * the consumer users table, one count aggregation per listed user — never the
   * documents themselves.
   */
  drawnRoutes: 'drawRoutes',
  routeCollections: 'RoutesCollections',
  /**
   * Skida PRO's own routes and tours. Owned by the company, not the guide —
   * 331 of 546 routes carry no `guideId` at all, which is why the PRO company
   * view attributes them to the company and shows the unattributed share.
   */
  proRoutes: 'skidaProRoutes',
  proTours: 'skidaProTours',
} as const
